import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import DemoPasswordModal from './DemoPasswordModal';
import { HelpModal } from '../ui/HelpModal';
import { HelpCircle } from 'lucide-react';
import { isNativeApp } from '../../utils/nativePlatform';
import { recordNativeDiagnostic } from '../../utils/nativeDiagnostics';

const LoginPage: React.FC = () => {
  const { 
    signIn, 
    isLoading, 
    authError, 
    isOidcAvailable, 
    isAuthenticated,
    signInDemoWithPassword,
    needsDemoPassword,
    setNeedsDemoPassword
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [callbackTimedOut, setCallbackTimedOut] = useState(false);

  // The native (iOS) app authenticates exclusively through the OIDC provider.
  // Web keeps its existing behavior: OIDC when available, otherwise Microsoft.
  const isNative = isNativeApp();
  const showOidcButton = isNative || isOidcAvailable;
  const showMicrosoftButton = !isNative && !isOidcAvailable;

  // While an auth redirect is being processed (native callback route or redirect
  // params present), show a loading screen instead of the sign-in buttons so the
  // login UI does not flash before navigation to the app completes. Once the user
  // is authenticated the callback is done: stop covering the screen so navigation
  // to /chat (or an overlaying mood-setup modal) is no longer hidden behind the
  // full-screen spinner.
  const hasRedirectParams =
    location.search.includes('code=') || location.search.includes('error=');
  const isProcessingCallback =
    !authError &&
    !callbackTimedOut &&
    !isAuthenticated &&
    (location.pathname === '/auth/callback' || hasRedirectParams);

  // Safety net: never spin forever. If the callback is still being processed after
  // a timeout, stop the spinner and reveal the sign-in options.
  useEffect(() => {
    if (location.pathname === '/auth/callback' || hasRedirectParams) {
      recordNativeDiagnostic('LoginPage processing callback', `path=${location.pathname}`);
      const timeout = setTimeout(() => {
        recordNativeDiagnostic('LoginPage callback timeout reached');
        setCallbackTimedOut(true);
      }, 12000);
      return () => clearTimeout(timeout);
    }
    setCallbackTimedOut(false);
    return undefined;
  }, [location.pathname, hasRedirectParams]);

  useEffect(() => {
    if (isNative && authError) {
      recordNativeDiagnostic('LoginPage authError', authError);
    }
  }, [authError, isNative]);

  useEffect(() => {
    // Native OIDC completes inside the app without a browser redirect back onto a new route.
    // If auth succeeded but the shared AuthContext navigation effect lags or gets blocked,
    // force the user off /login to a new chat (matching the web sign-in destination).
    if (!isNative || !isAuthenticated || authError) {
      return;
    }

    if (location.pathname === '/login' || location.pathname === '/auth/callback') {
      recordNativeDiagnostic('LoginPage native auth fallback redirect', `path=${location.pathname}`);
      navigate('/chat', { replace: true });
    }
  }, [authError, isAuthenticated, isNative, location.pathname, navigate]);


  const loadingSpinner = (
    <span className="flex items-center justify-center">
      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Signing in...
    </span>
  );

  const handleMicrosoftSignIn = async (): Promise<void> => {
    try {
      await signIn('msal');
    } catch (error) {
      console.error('Microsoft login failed:', error);
    }
  };

  const handleOidcSignIn = async (): Promise<void> => {
    try {
      recordNativeDiagnostic('LoginPage sign-in tapped', `path=${location.pathname}`);
      await signIn('oidc');
    } catch (error) {
      console.error('OIDC login failed:', error);
    }
  };

  if (isProcessingCallback) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-rm-bg-0 via-rm-bg-0 to-rm-primary-50 px-4">
        <svg className="animate-spin h-10 w-10 text-rm-primary-600 mb-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-rm-neutral-600 text-base">Signing you in…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rm-bg-0 via-rm-bg-0 to-rm-primary-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-rm-primary-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-rm-accent-100 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <Card className="shadow-2xl border-0 overflow-hidden relative">
          {/* Decorative top border */}
          <div className="h-1.5 bg-gradient-to-r from-rm-primary-500 via-rm-primary-600 to-rm-accent-500"></div>

          {/* About Kim - upper right corner */}
          <button
            onClick={() => setIsHelpModalOpen(true)}
            className="absolute top-6 right-4 z-10 inline-flex items-center gap-1.5 p-1 text-sm text-rm-primary-600 hover:text-rm-primary-700 font-medium transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            About Kim
          </button>
          
          <CardHeader className="text-center pt-12 pb-6 px-8">
            <div className="mb-6 inline-flex items-center justify-center mx-auto" role="img">
              <img 
                src="/Kim.png" 
                alt="Kim - Your Wellbeing Buddy" 
                className="w-32 h-32 rounded-full shadow-lg ring-4 ring-rm-primary-100"
              />
            </div>
            <CardTitle className="text-4xl font-bold text-rm-primary-700 leading-tight">
              Welcome to your<br/>
              <span className="text-rm-accent-600 font-extrabold">
                Wellbeing Buddy
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 px-8 pb-12">
            <p className="text-center text-base text-rm-neutral-600 leading-relaxed">
              Sign in with your account to chat with Kim 
            </p>

            {authError && (
              <div className="p-4 text-red-700 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">{authError}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {/* Renewmyday / OIDC Sign In (primary option, always used on native iOS) */}
              {showOidcButton && (
                <>
                  <Button
                    onClick={handleOidcSignIn}
                    disabled={isLoading}
                    variant="primary"
                    size="lg"
                    className="w-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                  >
                    {isLoading ? loadingSpinner : 'Sign in with Renewmyday'}
                  </Button>
                </>
              )}

              {/* Microsoft / Azure AD Sign In - web only, when OIDC is not available */}
              {showMicrosoftButton && (
                <Button
                  onClick={handleMicrosoftSignIn}
                  disabled={isLoading}
                  variant="primary"
                  size="lg"
                  className="w-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  {isLoading ? loadingSpinner : 'Sign in with Microsoft'}
                </Button>
              )}
            </div>
          </CardContent>

          {/* Footer */}
          
        </Card>
      </div>
      
      {/* Demo Password Modal */}
      <DemoPasswordModal
        isOpen={needsDemoPassword}
        onClose={() => setNeedsDemoPassword(false)}
        onSubmit={signInDemoWithPassword}
      />

      {/* Help / About Modal */}
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
};

export default LoginPage;
